import { useEffect, useState, type ReactNode } from "react";
import { FRIENDLY_API_ERROR } from "@/lib/api";
import {
  compactCount,
  formatDelta,
  formatScore,
  type RankProfile,
} from "@/lib/types";

type Phase = "idle" | "loading" | "ready" | "error";

const IDLE_LINES = [
  "hoodrank terminal v0.3",
  "awaiting handle…",
  "SCANNING FOLLOWER GRAPH…",
  "INDEXED KOLS: —",
  "INDEXED PROJECTS: —",
  "INDEXED FUNDS: —",
  "WATERMARK: 3,000 pts",
  "status: idle",
] as const;

const LOAD_TICKS = [
  "resolving handle…",
  "SCANNING FOLLOWER GRAPH…",
  "weighting influencers…",
  "weighting projects…",
  "weighting venture capital…",
  "computing reputation…",
] as const;

function BlinkCursor() {
  return (
    <span
      aria-hidden="true"
      className="ml-0.5 inline-block h-[1em] w-[0.55ch] translate-y-[0.12em] bg-accent align-baseline term-blink"
    />
  );
}

function TermLine({
  children,
  dim,
  accent,
}: {
  children: ReactNode;
  dim?: boolean;
  accent?: boolean;
}) {
  return (
    <p
      className={`mono text-[12px] leading-[1.65] tracking-[0.02em] sm:text-[13px] ${
        accent
          ? "text-accent-deep"
          : dim
            ? "text-ink-muted"
            : "text-ink-soft"
      }`}
    >
      <span className="mr-2 text-ink-muted select-none">›</span>
      {children}
    </p>
  );
}

/** Right-rail ASCII terminal — idle scan, then fills from a scored profile. */
export function TerminalReadout({
  phase,
  profile,
  handle,
  className = "hidden border border-ink bg-bg-panel lg:block",
}: {
  phase: Phase;
  profile?: RankProfile | null;
  handle?: string;
  className?: string;
}) {
  const [idleIdx, setIdleIdx] = useState(0);
  const [loadIdx, setLoadIdx] = useState(0);
  const [readyLines, setReadyLines] = useState(0);

  useEffect(() => {
    if (phase !== "idle") return;
    const id = window.setInterval(() => {
      setIdleIdx((n) => (n + 1) % IDLE_LINES.length);
    }, 1600);
    return () => window.clearInterval(id);
  }, [phase]);

  useEffect(() => {
    if (phase !== "loading") return;
    setLoadIdx(0);
    const id = window.setInterval(() => {
      setLoadIdx((n) => Math.min(n + 1, LOAD_TICKS.length - 1));
    }, 420);
    return () => window.clearInterval(id);
  }, [phase, handle]);

  useEffect(() => {
    if (phase !== "ready" || !profile) {
      setReadyLines(0);
      return;
    }
    setReadyLines(0);
    const total = 8;
    let n = 0;
    const id = window.setInterval(() => {
      n += 1;
      setReadyLines(n);
      if (n >= total) window.clearInterval(id);
    }, 90);
    return () => window.clearInterval(id);
  }, [phase, profile]);

  const target = (handle ?? "").replace(/^@/, "") || "—";

  return (
    <aside aria-live="polite" className={className}>
      <div className="flex items-center justify-between border-b border-line px-4 py-2">
        <p className="label">sys.out</p>
        <p className="mono text-[10px] uppercase tracking-[0.14em] text-ink-muted">
          {phase === "idle"
            ? "idle"
            : phase === "loading"
              ? "live"
              : phase === "ready"
                ? "ok"
                : "err"}
        </p>
      </div>

      <div className="relative p-4 font-mono">
        {phase === "idle" ? (
          <div className="space-y-0.5">
            {IDLE_LINES.map((line, i) => {
              const visible = i <= idleIdx;
              const active = i === idleIdx;
              return (
                <TermLine key={line} dim={!visible || !active}>
                  {visible ? line : "\u00a0"}
                  {active ? <BlinkCursor /> : null}
                </TermLine>
              );
            })}
          </div>
        ) : null}

        {phase === "loading" ? (
          <div className="space-y-0.5">
            <TermLine accent>
              target @{target}
              <BlinkCursor />
            </TermLine>
            {LOAD_TICKS.map((line, i) => (
              <TermLine key={line} dim={i > loadIdx}>
                {i <= loadIdx ? line : "\u00a0"}
                {i === loadIdx ? <BlinkCursor /> : null}
              </TermLine>
            ))}
            <TermLine dim>INDEXED KOLS: …</TermLine>
            <TermLine dim>INDEXED PROJECTS: …</TermLine>
            <TermLine dim>INDEXED FUNDS: …</TermLine>
          </div>
        ) : null}

        {phase === "ready" && profile ? (
          <ReadyBlock profile={profile} visible={readyLines} />
        ) : null}

        {phase === "error" ? (
          <div className="space-y-0.5">
            <TermLine accent>
              target @{target}
            </TermLine>
            <TermLine>SCAN FAILED</TermLine>
            <TermLine dim>{FRIENDLY_API_ERROR}</TermLine>
            <TermLine>
              retry with another handle
              <BlinkCursor />
            </TermLine>
          </div>
        ) : null}
      </div>
    </aside>
  );
}

function ReadyBlock({
  profile,
  visible,
}: {
  profile: RankProfile;
  visible: number;
}) {
  const s = profile.followersStats;
  const top = profile.topFollowers[0];
  const week = profile.scoreChanges.week_delta;
  const lines: { text: string; accent?: boolean; dim?: boolean }[] = [
    { text: `target @${profile.user.username}`, accent: true },
    { text: `SCORE ${formatScore(profile.score.score)} pts` },
    { text: `TIER ${profile.tierLabel.toUpperCase()}` },
    {
      text: `INDEXED KOLS: ${compactCount(s.influencers_count)}`,
    },
    {
      text: `INDEXED PROJECTS: ${compactCount(s.projects_count)}`,
    },
    {
      text: `INDEXED FUNDS: ${compactCount(s.venture_capitals_count)}`,
    },
    {
      text: `7D Δ ${week >= 0 ? "+" : "−"}${formatDelta(week)}`,
    },
    {
      text: top
        ? `TOP SIGNAL @${top.username}`
        : "TOP SIGNAL —",
      dim: !top,
    },
  ];

  return (
    <div className="space-y-0.5">
      {lines.map((line, i) => {
        const show = i < visible;
        const isTip = show && i === Math.min(visible, lines.length) - 1;
        return (
          <TermLine key={line.text} accent={line.accent} dim={line.dim || !show}>
            {show ? line.text : "\u00a0"}
            {isTip ? <BlinkCursor /> : null}
          </TermLine>
        );
      })}
      {visible >= lines.length ? (
        <TermLine dim>status: ready</TermLine>
      ) : null}
    </div>
  );
}
